
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fastingBenefits, fastingStages, fastingTips } from '@/data/fastingEducation';
import { BMICalculator } from '@/components/calculator/BMICalculator';
import { 
  Brain,
  Scale,
  Heart, 
  Clock, 
  Sparkles, 
  GlassWater, 
  Timer, 
  Sun, 
  Moon, 
  Apple, 
  ScrollText,
  FlameKindling,
  Dna,
  Zap,
  BrainCircuit,
  Trophy,
  Calculator
} from 'lucide-react';

export const LearnPage: React.FC = () => {
  // Helper function to render the appropriate icon for each benefit
  const getBenefitIcon = (title: string) => {
    switch(title) {
      case "Weight Loss":
        return <Scale className="h-8 w-8 text-green-500" />;
      case "Metabolic Health":
        return <FlameKindling className="h-8 w-8 text-orange-500" />;
      case "Cellular Repair":
        return <Dna className="h-8 w-8 text-purple-600" />;
      case "Brain Function":
        return <Brain className="h-8 w-8 text-blue-500" />;
      case "Heart Health":
        return <Heart className="h-8 w-8 text-red-500" />;
      case "Energy Levels":
        return <Zap className="h-8 w-8 text-yellow-500" />;
      default:
        return <Sparkles className="h-8 w-8 text-fasttrack-blue" />;
    }
  };

  // Helper function to render the appropriate icon for each stage
  const getStageIcon = (timeRange: string) => {
    if (timeRange.includes("0-12")) {
      return <Sun className="h-8 w-8 text-amber-500" />;
    } else if (timeRange.includes("12-24")) {
      return <Moon className="h-8 w-8 text-indigo-500" />;
    } else if (timeRange.includes("24-48")) {
      return <BrainCircuit className="h-8 w-8 text-purple-600" />;
    } else if (timeRange.includes("48-72")) {
      return <Dna className="h-8 w-8 text-green-600" />;
    } else {
      return <Trophy className="h-8 w-8 text-fasttrack-blue" />;
    }
  };

  // Helper function to render the appropriate icon for each tip
  const getTipIcon = (title: string) => {
    if (title.includes("Water")) {
      return <GlassWater className="h-8 w-8 text-blue-500" />;
    } else if (title.includes("Time")) {
      return <Clock className="h-8 w-8 text-amber-500" />;
    } else if (title.includes("Break")) {
      return <Apple className="h-8 w-8 text-green-500" />;
    } else if (title.includes("Track")) {
      return <ScrollText className="h-8 w-8 text-fasttrack-blue" />;
    } else {
      return <Timer className="h-8 w-8 text-purple-600" />;
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6 text-center">Learn About Fasting</h1>
      
      <Tabs defaultValue="benefits">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="benefits">Benefits</TabsTrigger>
          <TabsTrigger value="stages">Stages</TabsTrigger>
          <TabsTrigger value="tips">Tips</TabsTrigger>
          <TabsTrigger value="calculator">Calculator</TabsTrigger>
        </TabsList>
        
        <TabsContent value="benefits" className="space-y-4">
          {fastingBenefits.map((benefit, index) => (
            <Card key={index} className="overflow-hidden hover:shadow-md transition-all">
              <CardHeader className="pb-2 flex items-center">
                <div className="mr-4 bg-fasttrack-light-blue/20 p-3 rounded-full">
                  {getBenefitIcon(benefit.title)}
                </div>
                <CardTitle className="text-lg">{benefit.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
        
        <TabsContent value="stages" className="space-y-4">
          {fastingStages.map((stage, index) => (
            <Card key={index} className="overflow-hidden hover:shadow-md transition-all">
              <CardHeader className="pb-2 flex items-center">
                <div className="mr-4 bg-fasttrack-light-blue/20 p-3 rounded-full">
                  {getStageIcon(stage.timeRange)}
                </div>
                <CardTitle className="text-lg">
                  {stage.timeRange}: {stage.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{stage.description}</p>
                <div className="mt-2 text-sm font-medium">What's happening:</div>
                <ul className="list-disc list-inside text-sm text-muted-foreground pl-2">
                  {stage.effects.map((effect, i) => (
                    <li key={i}>{effect}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
        
        <TabsContent value="tips" className="space-y-4">
          {fastingTips.map((tip, index) => (
            <Card key={index} className="overflow-hidden hover:shadow-md transition-all">
              <CardHeader className="pb-2 flex items-center">
                <div className="mr-4 bg-fasttrack-light-blue/20 p-3 rounded-full">
                  {getTipIcon(tip.title)}
                </div>
                <CardTitle className="text-lg">{tip.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{tip.description}</p>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="calculator" className="space-y-4">
          <BMICalculator />
        </TabsContent>
      </Tabs>
      
      <div className="text-center text-xs text-muted-foreground mt-8 mb-4">
        Designed by <a href="https://wyattsanders.com" target="_blank" rel="noopener noreferrer" className="text-fasttrack-blue hover:underline">wyattsanders.com</a>
      </div>
    </div>
  );
};
